/**
 * ECharts choropleth renderer for the generic widget system.
 *
 * Supports two modes:
 *   groupBy === 'state'                  → US states map
 *   groupBy === 'congressional_district' → US 119th Congress CDs map
 *
 * Requires GeoJSON files served from /geo/us-states.geojson and
 * /geo/us-cd119.geojson. See public/geo/README.md for how they're produced
 * from the Census Bureau Cartographic Boundary files.
 *
 * Click on a region invokes onDrillDown(geoCode, label). The config-level
 * drill hierarchy (state → congressional_district) decides what happens next.
 */
import { useEffect, useState, useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import * as echarts from 'echarts/core'
import type { PerfRow, MetricKey, GroupByDimension } from './types'
import { METRIC_LABELS, formatMetric } from './types'

interface Props {
  data: PerfRow[]
  groupBy: GroupByDimension
  metrics: MetricKey[]
  onDrillDown?: (code: string, label: string) => void
  stateFilter?: string
}

type MapKind = 'us-states' | 'us-cd'

const MAP_URLS: Record<MapKind, string> = {
  'us-states': '/geo/us-states.geojson',
  'us-cd': '/geo/us-cd119.geojson',
}

// Module-level caches
const registered = new Set<MapKind>()
const pending = new Map<MapKind, Promise<void>>()
const geojsonCache = new Map<MapKind, GeoJSON.FeatureCollection>()

async function ensureMap(kind: MapKind): Promise<void> {
  if (registered.has(kind)) return
  if (pending.has(kind)) return pending.get(kind)!

  const p = (async () => {
    const resp = await fetch(MAP_URLS[kind])
    if (!resp.ok) throw new Error(`Failed to load ${MAP_URLS[kind]} (${resp.status})`)
    const geojson = await resp.json()
    echarts.registerMap(kind, geojson as Parameters<typeof echarts.registerMap>[1])
    geojsonCache.set(kind, geojson)
    registered.add(kind)
  })()
  pending.set(kind, p)
  try {
    await p
  } finally {
    pending.delete(kind)
  }
}

function rowKey(row: PerfRow, groupBy: GroupByDimension, stateFilter?: string): string {
  if (groupBy === 'state') {
    return String(row.state || row.state_code || '').toUpperCase()
  }
  const state = String(row.state || row.state_code || stateFilter || '').toUpperCase()
  const cd = String(row.congressional_district || '').padStart(2, '0')
  return `${state}-${cd}`
}

// Bounding box for contiguous US (excludes AK, HI, territories)
const CONUS_CENTER: [number, number] = [-96.5, 38.5]
const CONUS_ZOOM = 5.5

const STATE_CENTERS: Record<string, { center: [number, number]; zoom: number }> = {
  AL: { center: [-86.9, 32.8], zoom: 20 }, AK: { center: [-153.5, 64.2], zoom: 8 },
  AZ: { center: [-111.9, 34.3], zoom: 18 }, AR: { center: [-92.4, 34.8], zoom: 22 },
  CA: { center: [-119.7, 37.3], zoom: 16 }, CO: { center: [-105.5, 39.0], zoom: 18 },
  CT: { center: [-72.7, 41.6], zoom: 32 }, DE: { center: [-75.5, 39.0], zoom: 32 },
  DC: { center: [-77.0, 38.9], zoom: 55 }, FL: { center: [-82.5, 28.5], zoom: 16 },
  GA: { center: [-83.5, 32.7], zoom: 18 }, HI: { center: [-155.5, 19.9], zoom: 18 },
  ID: { center: [-114.7, 44.1], zoom: 16 }, IL: { center: [-89.4, 40.0], zoom: 18 },
  IN: { center: [-86.3, 40.0], zoom: 22 }, IA: { center: [-93.5, 42.0], zoom: 18 },
  KS: { center: [-98.5, 38.5], zoom: 18 }, KY: { center: [-85.8, 37.8], zoom: 18 },
  LA: { center: [-92.3, 31.0], zoom: 22 }, ME: { center: [-69.4, 45.3], zoom: 18 },
  MD: { center: [-76.6, 39.0], zoom: 26 }, MA: { center: [-71.8, 42.2], zoom: 26 },
  MI: { center: [-84.7, 44.3], zoom: 16 }, MN: { center: [-94.6, 46.4], zoom: 16 },
  MS: { center: [-89.7, 32.7], zoom: 18 }, MO: { center: [-92.6, 38.5], zoom: 18 },
  MT: { center: [-109.6, 47.0], zoom: 16 }, NE: { center: [-99.8, 41.5], zoom: 18 },
  NV: { center: [-116.4, 38.8], zoom: 16 }, NH: { center: [-71.6, 43.8], zoom: 26 },
  NJ: { center: [-74.4, 40.1], zoom: 26 }, NM: { center: [-106.2, 34.5], zoom: 16 },
  NY: { center: [-75.5, 43.0], zoom: 18 }, NC: { center: [-79.8, 35.5], zoom: 18 },
  ND: { center: [-100.5, 47.5], zoom: 18 }, OH: { center: [-82.8, 40.4], zoom: 22 },
  OK: { center: [-97.5, 35.5], zoom: 18 }, OR: { center: [-120.6, 43.9], zoom: 16 },
  PA: { center: [-77.6, 41.2], zoom: 22 }, RI: { center: [-71.5, 41.7], zoom: 36 },
  SC: { center: [-81.0, 33.8], zoom: 24 }, SD: { center: [-100.3, 44.5], zoom: 18 },
  TN: { center: [-86.3, 35.8], zoom: 18 }, TX: { center: [-99.3, 31.5], zoom: 14 },
  UT: { center: [-111.7, 39.3], zoom: 18 }, VT: { center: [-72.6, 44.0], zoom: 26 },
  VA: { center: [-79.4, 37.8], zoom: 22 }, WA: { center: [-120.7, 47.4], zoom: 18 },
  WV: { center: [-80.6, 38.9], zoom: 24 }, WI: { center: [-89.8, 44.6], zoom: 18 },
  WY: { center: [-107.6, 43.0], zoom: 18 },
}

export default function MapChartRenderer({ data, groupBy, metrics, onDrillDown, stateFilter }: Props) {
  const mapKind: MapKind = groupBy === 'congressional_district' ? 'us-cd' : 'us-states'
  const [ready, setReady] = useState(registered.has(mapKind))
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoadError(null)
    if (registered.has(mapKind)) {
      setReady(true)
      return
    }
    setReady(false)
    ensureMap(mapKind)
      .then(() => { if (alive) setReady(true) })
      .catch((err) => { if (alive) setLoadError(err.message || 'Failed to load map') })
    return () => { alive = false }
  }, [mapKind])

  // Compute zoom center based on state filter
  const geoSettings = useMemo(() => {
    if (stateFilter && STATE_CENTERS[stateFilter]) {
      const { center, zoom } = STATE_CENTERS[stateFilter]
      return { center, zoom }
    }
    return { center: CONUS_CENTER, zoom: CONUS_ZOOM }
  }, [stateFilter])

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-red-400 p-4 text-center">
        Map data unavailable.<br/>
        <span className="text-xs text-gray-400 mt-2">
          {loadError} — see public/geo/README.md for how to generate.
        </span>
      </div>
    )
  }

  if (!ready) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        Loading map…
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        No data
      </div>
    )
  }

  const metric = metrics[0]

  const values = data.map((r) => {
    const val = r[metric]
    const numVal = typeof val === 'number' ? val : parseFloat(String(val)) || 0
    return {
      name: rowKey(r, groupBy, stateFilter),
      value: numVal,
      _row: r,
    }
  })

  const nums = values.map((v) => v.value).filter((n) => n > 0)
  const min = nums.length ? Math.min(...nums) : 0
  const max = nums.length ? Math.max(...nums) : 1

  const option = {
    backgroundColor: '#f0f0f0',
    tooltip: {
      trigger: 'item',
      formatter: (params: { name: string; value: number; data?: { _row?: PerfRow } }) => {
        const row = params.data?._row
        const label = row
          ? (groupBy === 'state'
              ? String(row.state || row.state_code || params.name)
              : `${String(row.state || row.state_code || '')}-${String(row.congressional_district || '')}`)
          : params.name
        const valueStr = isNaN(params.value)
          ? 'No data'
          : formatMetric(metric, params.value)
        return `<b>${label}</b><br/>${METRIC_LABELS[metric]}: <b>${valueStr}</b>`
      },
    },
    visualMap: {
      left: 'left',
      bottom: 20,
      min,
      max,
      text: ['High', 'Low'],
      realtime: false,
      calculable: true,
      inRange: {
        color: ['#dbeafe', '#60a5fa', '#1d4ed8', '#1e3a8a'],
      },
      textStyle: { fontSize: 10 },
    },
    series: [
      {
        name: METRIC_LABELS[metric],
        type: 'map',
        map: mapKind,
        roam: true,
        center: geoSettings.center,
        zoom: geoSettings.zoom,
        emphasis: {
          label: { show: false },
          itemStyle: { areaColor: '#f59e0b' },
        },
        itemStyle: {
          areaColor: '#e5e7eb',
          borderColor: '#9ca3af',
          borderWidth: 0.8,
        },
        data: values,
      },
    ],
  }

  const onEvents = onDrillDown
    ? {
        click: (params: { data?: { _row?: PerfRow; name?: string } }) => {
          const row = params.data?._row
          if (!row) return
          const code = groupBy === 'state'
            ? String(row.state || row.state_code || '')
            : String(row.congressional_district || '')
          const label = groupBy === 'state'
            ? String(row.state || row.state_code || '')
            : `${String(row.state || row.state_code || '')}-${String(row.congressional_district || '')}`
          if (code) onDrillDown(code, label)
        },
      }
    : undefined

  return (
    <ReactECharts
      key={mapKind}
      option={option}
      style={{ height: '100%', width: '100%' }}
      opts={{ renderer: 'canvas' }}
      onEvents={onEvents}
      notMerge={true}
    />
  )
}
